/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  FormEvent,
  MouseEvent,
  SyntheticEvent,
  useEffect,
  useState
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {
  AssetMoveEditorFormFields
} from '@ion/core/src/types/editors';
import {
  Account,
  Asset
} from '@ion/core/src/types/entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import Chip from '@ion/dls/src/components/chip/Chip';
import FormGroupFields from '@ion/dls/src/components/forms/FormGroupFields';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {
  useAnimatedMount
} from '../hooks/useAnimatedMount';
import {
  useExtendStyles
} from '../hooks/useExtendStyles';
import {useFormState} from '../hooks/useFormState';
import {
  useMedia
} from '../hooks/useMedia';
import {
  useMergeState
} from '../hooks/useMergeState';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {DLSTheme, IconSize} from '../types/types';
import {verifyCommonAttributes} from './AssetBulkEditorUtils';
import {deserialize, serialize} from './AssetEditorUtils';
import EditorDialog from './EditorDialog';
import styles from './editorStyles';
import GroupChooser from './GroupChooser';

const styleOverrides = (theme: DLSTheme) => ({
  chipContainer : {
    display  : 'flex',
    flexWrap : 'wrap'
  },
  chipWrapper : {
    marginBottom : '10px',
    position     : 'relative'
  },
  targetAccount : {
    marginTop : '-50px'
  }
});

const dialogOverrides = (theme: DLSTheme) => ({
  form : {
    height : '100%'
  },
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '2px !important',
    height      : '521px',
    minHeight   : 'unset !important'
  },
  paperScrollPaper : {
    [theme.breakpoints.down('sm')] : {
      minHeight : '100%'
    }
  }
});

// defines the Asset bulk editor prop type definitions ...
interface AssetMoveEditorProps {
  open: boolean;
  assets?: Asset[];
  account: Account;
  clear?: boolean;
  loading?: boolean;
  onSave: (assets: Asset[]) => void;
  onCancel?: (event: SyntheticEvent<{}, Event>) => void;
  isCtcAdmin?: boolean;
}

// defines the text input prop type definitions ...
interface TargetType {
  name: AssetMoveEditorFormFields,
  value: unknown;
  required?: boolean
}

/**
 * The Asset Bulk edit Form with set fields and encapsulated logic for handling the form's state
 *
 * @param {boolean} open Determines if the AssetMoveEditor dialog should be open
 * @param {object} account User's account object
 * @param {Function} onCancel Callback function fired when the dialog is to be closed.
 * @param {Function} onSave Function the be run when the form is submitted.
 *   The function is passed an asset object
 * @param {Asset[]} assets Assets that passed to the form
 * @param {boolean} clear If true we clear the form state
 * @param {boolean} loading disables any for submission while the form is busy
 */
export const AssetMoveEditor = ({
  open,
  account,
  onSave,
  onCancel,
  assets = [],
  clear = false,
  loading = false,
  isCtcAdmin = false
}: AssetMoveEditorProps) => {
  const editorStyles                          = useExtendStyles(styles, styleOverrides);
  const classes                               = editorStyles();
  const {isPhone}                             = useMedia();
  const {isVisible, onRemove}                 = useAnimatedMount();
  const [formState, setFormState]             = useFormState();
  const [assetDatas, setAssetDatas]           = useState<Asset[]>();
  const [groupsLoading, setGroupsLoading]     = useState(false);
  const [groupValue, setGroupValue]           = useState(null);
  const [selectedAssets, setSelectedAssets]   = useState([]);
  const [openDatePickers, setOpenDatePickers] = useMergeState();
  const [isFormChanged, setIsFormChanged]     = useState(false);
  const entityLabel                           = translate('common:entityLabels.assets');

  useEffect(() => {
    if (!isObjectEmpty(assets) && open) {
      const deserializedAssets = assets.map(asset => deserialize(asset));
      setAssetDatas(deserializedAssets);
      setFormState(verifyCommonAttributes(deserializedAssets));
      setIsFormChanged(false);
      setSelectedAssets(assets);
    }
  }, [open]);

  useEffect(() => {
    if (clear) {
      clearForm();
    }
  }, [clear]);

  function clearForm() {
    setFormState({}, true);
    setIsFormChanged(false);
  };

  function handleChooserChange({name, value, required = false}) {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
    setIsFormChanged(true);
  };

  function handleSubmit(e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const assetSerializeDatas = assetDatas?.map(asset => {
      const newAsset = {...asset, ...formState};
      if (isCtcAdmin) {
        newAsset.updateAccount =  {name: 'updateAccount', value: true};
      }
      return serialize(newAsset);
    });
    onSave?.(assetSerializeDatas);
  }

  const onClose = function() {
    onRemove(onCancel);
  };

  function onChipDelete(option) {
    removeAsset(option);
  };

  const removeAsset = (selectedItem) => {
    const filteredTools = selectedAssets.filter((tool) => {
      return tool.id !== selectedItem.id;
    });
    const deserializedAssets = filteredTools.map(asset => deserialize(asset));
    setAssetDatas(deserializedAssets);
    setSelectedAssets(filteredTools);
  };

  return (
    <EditorDialog open={isVisible}
      dialogTitle={translate('common:editors.move.title', {
        entity : entityLabel
      })}
      onCloseDialog={onClose}
      onCancelDialog={onClose}
      onPrimaryButtonClick={handleSubmit}
      primaryButtonDisabled={!isFormChanged}
      primaryButtonLoading={loading}
      styleOverrides={dialogOverrides} >
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.formWrapper}>
          <FormSection title={translate('admin-assets:form.move.selected')}>
            <FormGroupFields>
              <FormItem fullWidth>
                <div className={classes.chipContainer}>
                  {selectedAssets?.length > 1 && selectedAssets.map((selected, index) => {
                    return (
                      <div key={selected.id} className={classes.chipWrapper}>
                        <Chip label={selected.name}
                          size='small'
                          maxWidth={false}
                          borderRadius='56px'
                          labelColor={dlsColor('grey2')}
                          hoverDefault
                          showCustomDeleteIcon
                          noTextTransform
                          customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
                          onDelete={function() {
                            onChipDelete(selected);
                          }}/>
                      </div>);
                  })}
                  {selectedAssets?.length === 1 && selectedAssets.map((selected, index) => {
                    return (
                      <div key={selected.id} className={classes.chipWrapper}>
                        <Chip label={selected.name}
                          size='small'
                          maxWidth={false}
                          borderRadius='56px'
                          labelColor={dlsColor('grey2')}
                          hoverDefault
                          showCustomDeleteIcon
                          noTextTransform/>
                      </div>);
                  })}
                </div>
              </FormItem>
            </FormGroupFields>
          </FormSection>
          <div className={classes.targetAccount}>
            <FormSection title={translate('admin-assets:form.move.targetAccount')}>
              <FormGroupFields>
                <FormItem fullWidth>
                  <GroupChooser onLoad={setGroupsLoading}
                    includeGroupProperties={false}
                    onChange={function(value) {
                      setGroupValue(value);
                      handleChooserChange({
                        name : AssetMoveEditorFormFields.Groups,
                        value
                      });
                    }}
                    hasError={formState[AssetMoveEditorFormFields.Groups]?.error}
                    defaultValue={formState[AssetMoveEditorFormFields.Groups]?.value} />
                </FormItem>
              </FormGroupFields>
            </FormSection>
          </div>
        </div>
      </form>
    </EditorDialog>
  );
};

export default AssetMoveEditor;
